import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LanguageContext } from "../../globals/LanguageContext";
import { StyledForm } from "./Form.styled";
import { useProfile } from "../../stores/useProfile";
import { FormProps } from "../../models/global/Types";
import { Project, Tag } from "../../models/Project";
import { useProject } from "../../stores/useProject";
import { ArrowClockwise } from "@phosphor-icons/react";

interface ProjectFormProps extends FormProps {
  project?: Project;
  projectList: Project[];
}

export const ProjectForm = ({
  onSave,
  project,
  projectList,
}: ProjectFormProps) => {
  const [newTagInputVisible, setNewTagInputVisible] = useState(false);
  const [newTagValue, setNewTagValue] = useState("");
  const [generatedCode, setGeneratedCode] = useState(project?.code || "");
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const {
    handleSubmit,
    control,
    register,
    setValue,
    formState: { errors },
  } = useForm<Project>({});
  const { dictionary } = useContext(LanguageContext);

  const { user } = useProfile();
  const { addProject, editProject } = useProject();

  const onSubmit = async (data: Project) => {
    if (!user) return;
    project
      ? await editProject(user, data, project.id!)
      : await addProject(user, data);
    onSave();
  };

  const allTags = useMemo(() => {
    const tagMap = new Map<string, (typeof projectList)[0]["tags"][0]>();

    projectList.forEach((project) => {
      project.tags.forEach((tag) => {
        tagMap.set(tag.name, tag);
      });
    });

    return Array.from(tagMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [projectList]);

  useEffect(() => {
    setAvailableTags(allTags);
  }, [allTags]);

  const handleAddTag = () => {
    const tagName = newTagValue.trim();
    if (
      tagName &&
      !availableTags.some(
        (tag) => tag.name.toLowerCase() === tagName.toLowerCase()
      )
    ) {
      const newTag = {
        id: Date.now(),
        name: tagName,
      };
      setAvailableTags([...availableTags, newTag]);
      setNewTagValue("");
      setNewTagInputVisible(false);
    }
  };

  const generateUniqueCode = useCallback(() => {
    const existingCodes = new Set(projectList.map((p) => p.code));

    const generateCode = () => {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let result = "";
      for (let i = 0; i < 4; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    };

    let newCode = generateCode();
    while (existingCodes.has(newCode)) {
      newCode = generateCode();
    }

    setValue("code", newCode, { shouldValidate: true });
    return newCode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectList]);

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <Box className="row">
        <Box className="column">
          <Typography variant="h4">{dictionary.projectform.code}</Typography>
          <Box className="row">
            <TextField
              {...register("code", {
                required: dictionary.errormessages.form_fieldrequired,
              })}
              value={generatedCode}
              onChange={(e) => setGeneratedCode(e.target.value)}
              error={!!errors.code}
              helperText={errors?.code?.message}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Button onClick={() => setGeneratedCode(generateUniqueCode())}>
              <ArrowClockwise size={32} />
            </Button>
          </Box>
        </Box>
      </Box>
      <Box className="row">
        <Box className="column">
          <Typography variant="h4">
            {dictionary.projectform.description}
          </Typography>
          <TextField
            {...register("description", {
              required: dictionary.errormessages.form_fieldrequired,
            })}
            error={!!errors.description}
            helperText={errors?.description?.message}
            InputLabelProps={{ shrink: true }}
            fullWidth
            defaultValue={project?.description}
          />
        </Box>
      </Box>
      <Box className="row">
        <Box className="column">
          <Typography variant="h4">{dictionary.projectform.status}</Typography>
          <FormControl fullWidth error={!!errors.status}>
            <InputLabel shrink>{dictionary.projectform.status}</InputLabel>
            <Select
              defaultValue={project?.status || ""}
              {...register("status", {
                required: dictionary.errormessages.form_fieldrequired,
              })}
              displayEmpty
              notched
            >
              <MenuItem value="">
                <em>
                  {dictionary.projectform.choosestatus || "Choose status"}
                </em>
              </MenuItem>
              <MenuItem value="Created">Created</MenuItem>
              <MenuItem value="QuoteSent">Quote Sent</MenuItem>
              <MenuItem value="InProgress">In Progress</MenuItem>
              <MenuItem value="Finished">Finished</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </Select>
            <FormHelperText>{errors?.status?.message}</FormHelperText>
          </FormControl>
        </Box>
      </Box>
      <Box className="row">
        <Box className="column">
          <Typography variant="h4">{dictionary.projectform.tags}</Typography>

          <FormControl fullWidth error={!!errors.tags}>
            <InputLabel shrink>{dictionary.projectform.tags}</InputLabel>

            <Controller
              name="tags"
              control={control}
              defaultValue={project?.tags || []}
              rules={{ required: dictionary.errormessages.form_fieldrequired }}
              render={({ field }) => {
                const selectedIds = field.value.map((tag: Tag) => tag.id);

                const handleChange = (event: any) => {
                  const selectedTagIds = event.target.value as number[];
                  const selectedTags = availableTags.filter((tag) =>
                    selectedTagIds.includes(tag.id)
                  );
                  field.onChange(selectedTags);
                };

                return (
                  <Select
                    multiple
                    value={selectedIds}
                    onChange={handleChange}
                    input={
                      <OutlinedInput
                        notched
                        label={dictionary.projectform.tags}
                      />
                    }
                    renderValue={(selected: number[]) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((id) => {
                          const tag = availableTags.find((t) => t.id === id);
                          return <Chip key={id} label={tag?.name || id} />;
                        })}
                      </Box>
                    )}
                  >
                    {availableTags.map((tag) => (
                      <MenuItem key={tag.id} value={tag.id}>
                        <Checkbox checked={selectedIds.includes(tag.id)} />
                        <ListItemText primary={tag.name} />
                      </MenuItem>
                    ))}
                  </Select>
                );
              }}
            />
            <FormHelperText>{errors?.tags?.message}</FormHelperText>
          </FormControl>

          <Box mt={1}>
            {!newTagInputVisible ? (
              <Button
                variant="outlined"
                onClick={() => setNewTagInputVisible(true)}
              >
                + {dictionary.projectform.addtag || "Add Tag"}
              </Button>
            ) : (
              <Box display="flex" gap={1} mt={1}>
                <TextField
                  label={dictionary.projectform.newtag || "New tag"}
                  value={newTagValue}
                  onChange={(e) => setNewTagValue(e.target.value)}
                  size="small"
                />
                <Button variant="contained" onClick={handleAddTag}>
                  {dictionary.projectform.add || "Add"}
                </Button>
                <Button
                  onClick={() => {
                    setNewTagInputVisible(false);
                    setNewTagValue("");
                  }}
                >
                  {dictionary.projectform.cancel || "Cancel"}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Button type="submit">{dictionary.buttons.savebtn}</Button>
    </StyledForm>
  );
};
